import type SessionService from '@efdigital/ll-addon-auth/services/session';
import type {
  CoachingPartnership,
  CoachingSession,
} from '@efdigital/ll-addon-data';
import { queryCoachingPartnershipsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships';
import { queryCoachingSessionsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type { CoachingCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import {
  buildCoacheeList,
  COACH_COACHEE_PARTNERSHIP_KEYS,
  type CoacheeListEntry,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

export type CoacheeEntry = CoacheeListEntry<
  CoachingPartnership,
  CoachingSession
>;

export type CoachingCoachCoacheesModel = CoachingCoachModel & {
  coachees: CoacheeEntry[];
};

/**
 * The coach's coachees, loaded once for both the list and a coachee's page.
 *
 * Sessions come back unfiltered by status so each coachee's page can group them
 * itself, and so the list can show a coachee's next session without a second pass.
 */
export default class CoachingCoachCoacheesRoute extends Route<CoachingCoachCoacheesModel> {
  @service declare coach: CoachService;
  @service declare store: Store;
  @service declare session: SessionService;

  async model(): Promise<CoachingCoachCoacheesModel> {
    const coachModel = this.modelFor('coaching.coach') as CoachingCoachModel;
    const coachUserId = this.session.cached.userId!;

    const [partnerships, sessions, coacheeNames] = await Promise.all([
      this.store.request(
        queryCoachingPartnershipsByCoach(
          coachUserId,
          COACH_COACHEE_PARTNERSHIP_KEYS
        )
      ),
      this.store.request(queryCoachingSessionsByCoach(coachUserId)),
      this.coach.getCoachees(),
    ]);

    return {
      ...coachModel,
      coachees: buildCoacheeList(
        partnerships.content.data,
        sessions.content.data,
        coacheeNames
      ),
    };
  }
}
